// Bell screen — paged in-app notifications with a "Mark all read" action.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateTime } from "@/lib/format";
import { useNotifications } from "@/lib/notifications";
import {
  fetchMyNotifications,
  markNotificationsRead,
  type AppNotificationRow,
} from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 20;

export default function Notifications() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, refresh, setUnreadCount } = useNotifications();

  const [rows, setRows] = useState<AppNotificationRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [marking, setMarking] = useState(false);

  const requestSeq = useRef(0);

  const load = useCallback(
    (pageToLoad: number, append: boolean) => {
      if (!token) return;
      const seq = ++requestSeq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      fetchMyNotifications(token, { currentPage: pageToLoad, pageSize: PAGE_SIZE })
        .then((d) => {
          if (seq !== requestSeq.current) return;
          setRows((prev) => (append ? [...prev, ...(d.rows ?? [])] : d.rows ?? []));
          setTotalCount(d.totalCount ?? 0);
          setPage(d.currentPage ?? pageToLoad);
          setUnreadCount(d.unreadCount ?? 0);
        })
        .catch((e) => {
          if (seq !== requestSeq.current) return;
          toast.error(e instanceof ApiError ? e.message : "Could not load notifications.");
        })
        .finally(() => {
          if (seq !== requestSeq.current) return;
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [token, setUnreadCount]
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  function markAllRead() {
    if (!token) return;
    setMarking(true);
    markNotificationsRead(token, { all: true })
      .then(() => {
        load(1, false); // refresh the list …
        refresh(); // … and the shared badge
      })
      .catch((e) => {
        toast.error(e instanceof ApiError ? e.message : "Could not mark notifications read.");
      })
      .finally(() => setMarking(false));
  }

  function open(n: AppNotificationRow) {
    if (n.link?.startsWith("/")) navigate(n.link);
  }

  const hasMore = rows.length < totalCount;

  return (
    <div className="flex flex-col gap-3 p-4">
      {unreadCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="self-end"
          disabled={marking}
          onClick={markAllRead}
        >
          <CheckCheck /> {marking ? "Marking…" : "Mark all read"}
        </Button>
      )}

      {loading ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <Bell className="size-8 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium">No notifications</p>
          <p className="text-xs text-muted-foreground">
            Updates about your payment requests will appear here.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((n) => (
            <li key={n.id}>
              <NotificationCard row={n} onOpen={() => open(n)} />
            </li>
          ))}
        </ul>
      )}

      {!loading && hasMore && (
        <Button
          variant="outline"
          className="w-full"
          disabled={loadingMore}
          onClick={() => load(page + 1, true)}
        >
          {loadingMore ? "Loading…" : `Load more (${rows.length} of ${totalCount})`}
        </Button>
      )}
    </div>
  );
}

function NotificationCard({ row, onOpen }: { row: AppNotificationRow; onOpen: () => void }) {
  const clickable = !!row.link?.startsWith("/");
  return (
    <Card
      size="sm"
      className={cn(
        "transition-colors",
        clickable && "cursor-pointer active:bg-muted/50",
        !row.isRead && "border-primary/30 bg-primary/5"
      )}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <CardContent className="flex gap-3">
        {!row.isRead && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
        <div className="min-w-0 flex-1">
          <div className={cn("text-sm", row.isRead ? "font-medium" : "font-semibold")}>
            {row.title || "Notification"}
          </div>
          {row.body && <div className="mt-0.5 text-xs text-muted-foreground">{row.body}</div>}
          {row.dateCreated && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              {formatDateTime(row.dateCreated)}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card size="sm" key={i}>
          <CardContent className="flex flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-24" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
